/**
 * 「AIをつなぐ」の絵の中身（どの AI でどの段の絵を出すか・選んだ AI を覚える）。純関数だけ（テストは connectPictures.test.ts）。
 * 描くのは components/ConnectPictures.tsx（2026-10-19 design/visual-guides・founder「文字ばっかりだな。図解とかスクショを使おうよ」）。
 *
 * WHY AI ごとに 4 段か: 貼る文（webConnect.ts）は 1 つだけ = AI が手順を教える。それでも人は「この後どんな画面が出て、どこを押すのか」が
 *   分からず止まる（Ops: 確認した人の多くが AI をつながない）。押す所の形を先に見せて、AI の案内と同じ形の画面が来ると分かるようにする。
 * 段の中身はサイトの手順のページ（content/en/claude-and-chatgpt.md と src/screens.mjs）と同じ事実。押す所の名前が変わったら両方を直す。
 * パソコンの AI エージェント（Claude Code・Codex・Cursor）は同じ流れ（貼る → connect のコマンド → ブラウザで Allow → 頼む）。
 *   違うのは窓の形だけ（端末 / エディタ）なので、段は共有して窓の種類（frame）だけ変える。
 */

export type ConnectAi = 'chatgpt' | 'claude' | 'claudeCode' | 'codex' | 'cursor';
export type PicStep = 'paste' | 'chatgptAdd' | 'claudeAdd' | 'allow' | 'chatgptUse' | 'claudeUse' | 'runs' | 'ask';
/** 窓の形: web = ブラウザ（アドレスの欄）・terminal = 端末・editor = 横に会話の欄があるエディタ */
export type PicFrame = 'web' | 'terminal' | 'editor';

export interface ConnectAiInfo {
  id: ConnectAi;
  /** 画面に出す名前（製品名なので訳さない） */
  name: string;
  frame: PicFrame;
  /** web の窓のアドレスの欄に出す文字（本物のロゴの代わり）。端末・エディタは窓の題 */
  where: string;
  steps: readonly PicStep[];
}

// 並び = 指示書の順（ChatGPT・Claude の Web・Claude Code・Codex・Cursor）。最初の ChatGPT が既定
//   WHY ChatGPT が既定か: 広告（Reddit）から来る人の多くは Web の AI でゲームを作った人（gtm/early-review.md）。パソコンの AI エージェントの人は名前で選び直せる
const LOCAL_STEPS = ['paste', 'runs', 'allow', 'ask'] as const;
export const CONNECT_AIS: readonly ConnectAiInfo[] = [
  { id: 'chatgpt', name: 'ChatGPT', frame: 'web', where: 'chatgpt.com', steps: ['paste', 'chatgptAdd', 'allow', 'chatgptUse'] },
  { id: 'claude', name: 'Claude', frame: 'web', where: 'claude.ai', steps: ['paste', 'claudeAdd', 'allow', 'claudeUse'] },
  { id: 'claudeCode', name: 'Claude Code', frame: 'terminal', where: 'Claude Code', steps: LOCAL_STEPS },
  { id: 'codex', name: 'Codex', frame: 'terminal', where: 'Codex', steps: LOCAL_STEPS },
  { id: 'cursor', name: 'Cursor', frame: 'editor', where: 'Cursor', steps: LOCAL_STEPS }
];

/** 既定 = 並びの最初（ChatGPT）。配列の [0] は型で undefined になりうるので名前で持つ */
const DEFAULT_AI: ConnectAiInfo = CONNECT_AIS[0] as ConnectAiInfo;

/** 選んだ AI を覚える場所（この端末だけの便利・無くても既定で正しく出る）。読めない・知らない値は既定 */
export const CONNECT_AI_KEY = 'kumo.console.connectAi';

export function readConnectAi(storage: { getItem(k: string): string | null } | null): ConnectAi {
  let v: string | null = null;
  try {
    v = storage?.getItem(CONNECT_AI_KEY) ?? null;
  } catch {
    v = null;
  }
  return CONNECT_AIS.find((a) => a.id === v)?.id ?? DEFAULT_AI.id;
}

export function saveConnectAi(storage: { setItem(k: string, v: string): void } | null, ai: ConnectAi): void {
  try {
    storage?.setItem(CONNECT_AI_KEY, ai);
  } catch {
    /* プライベートモード等。覚えないだけ */
  }
}

export function connectAiInfo(ai: ConnectAi): ConnectAiInfo {
  return CONNECT_AIS.find((a) => a.id === ai) ?? DEFAULT_AI;
}
